import icn_linebar_chart from "assets/images/icons/icn_linebar_chart.svg";
import icn_info_solid from "assets/images/icons/icn_Info_solid.svg";
import { ReactComponent as Clock } from "assets/images/icons/icn_display_clock.svg";
import { useTheme } from "@mui/material/styles";

import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Tooltip,
  tooltipClasses,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import WiredCableQualityChart from "../../charts/WiredCableQualityChart";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#4D4D4D",
    color: theme.palette.primary.white,
    maxWidth: 600,
    fontSize: theme.typography.pxToRem(12),
    padding: "16px 20px",
    borderRadius: "8px",
    lineHeight: "1.5",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
  },
}));

const ActionList = ({ items }) => (
  <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
    {items.map((item, index) => (
      <Box component="li" key={index} sx={{ mb: 0.5 }}>
        {item}
      </Box>
    ))}
  </Box>
);

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ whiteSpace: "pre-line", fontSize: "12px", mb: 1 }}>
        {`· Note: A port is an Error Port when it accumulates more than 50 CRC errors in 1 hour or more than 1,200 in 24 hours. Health is the percentage of Error Ports among all linked-up ports at the site.​​`}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Good — Error Ports &lt; 5%​
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        Physical cabling is healthy. Data frames transmit cleanly across the
        vast majority of links.​
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Warning — 5% ≤ Error Ports &lt; 10%
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px" }}>
        Some ports are accumulating CRC errors — early signs of cable aging,
        poor termination, or interference.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Recommended Actions
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        <ActionList
          items={[
            "· Re-seat connectors and replace suspect patch cords with known-good cables.​",
          ]}
        />
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Critical — Error Ports ≥ 10%
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px" }}>
        Widespread CRC errors indicate systemic cabling issues, severe
        interference, or failing hardware
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Recommended Actions
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
        <ActionList
          items={[
            "· Run a Cable Test (TDR) on the affected runs.​",
            "· Inspect cable paths and swap switch ports to rule out failing hardware.​",
          ]}
        />
      </Typography>
    </Box>
  </Box>
);

const WiredCableQuality = ({
  loading = false,
  noData = false,
  onClick,
  wiredCableQualityChartSeriesData = [],
  wiredCableQualityApiChartData,
}) => {
  const theme = useTheme();

  const statusColorMap = {
    Good: theme.palette.primary.greenPrimary,
    Warning: theme.palette.primary.orangeThird,
    Critical: theme.palette.primary.redPrimary,
  };

  const isNoData = noData || Number(wiredCableQualityApiChartData?.switchCount) === 0;
  
  const status = useMemo(() => {
    const linkUpPorts = Number(wiredCableQualityApiChartData.linkUpPorts) || 0;
    const noisyPorts = Number(wiredCableQualityApiChartData.noisyPorts) || 0;

    if (linkUpPorts === 0) return "Good";

    const errorRate = (noisyPorts / linkUpPorts) * 100;

    if (errorRate >= 10) return "Critical";
    if (errorRate >= 5) return "Warning";
    return "Good";
  }, [wiredCableQualityApiChartData]);

  return (
    <Box
      onClick={isNoData ? undefined : onClick}
      sx={{
        border: `1px solid ${theme.palette.primary.greyThread}`,
        borderRadius: theme.shape.customBorderRadius[8],
        p: 2.5,
        bgcolor: theme.palette.primary.white,
        mb: 3,
        cursor: onClick && !isNoData ? "pointer" : "default",
        "&:hover":
          onClick && !isNoData
            ? {
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
              }
            : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src={icn_linebar_chart}
            sx={{ width: "18px", height: "18px", mr: 1 }}
          />

          <Typography
            sx={{
              fontWeight: theme.typography.customFontWeight[2],
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[3],
              mr: 1,
            }}
          >
            Wired Cable Quality
          </Typography>

          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
          </HtmlTooltip>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
              ml: 0.5,
            }}
          >
            Last 24 Hours
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            height: "250px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : isNoData ? (
        <Box
          sx={{
            height: "250px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.greyThird,
              fontSize: theme.typography.customFontSize[2],
            }}
          >
            No data available
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: 280 },
              pr: { xs: 0, lg: 5 },
              pb: { xs: 3, lg: 0 },
              borderRight: {
                xs: "none",
                lg: `1px solid ${theme.palette.primary.greyThread}`,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 6,
                mb: 2.5,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[1],
                    fontWeight: theme.typography.customFontWeight[3],
                    color: theme.palette.primary.greyPrimary,
                    mb: 0.5,
                  }}
                >
                  Status
                </Typography>

                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[6],
                    lineHeight: 1.1,
                    fontWeight: theme.typography.customFontWeight[3],
                    color: statusColorMap[status],
                  }}
                >
                  {status}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[1],
                    fontWeight: theme.typography.customFontWeight[3],
                    color: theme.palette.primary.greyPrimary,
                    mb: 0.5,
                  }}
                >
                  Noisy Port(s)
                </Typography>

                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[11],
                    lineHeight: 1.1,
                    fontWeight: theme.typography.customFontWeight[3],
                    color: theme.palette.primary.main,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: theme.typography.customFontSize[6],
                      fontWeight: theme.typography.customFontWeight[3],
                    }}
                  >
                    {wiredCableQualityApiChartData.noisyPorts}
                  </Box>{" "}
                  Port(s)
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                borderTop: `1px solid ${theme.palette.primary.greyThread}`,
                pt: 2.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greyPrimary,
                  mb: 1,
                  fontWeight: theme.typography.customFontWeight[3],
                }}
              >
                Impacted Switch:{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.primary.mainHover,
                    fontWeight: theme.typography.customFontWeight[3],
                  }}
                >
                  {wiredCableQualityApiChartData.switchesWithErrorPorts}
                </Box>
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              pl: { xs: 0, lg: 5 },
              pt: { xs: 1, lg: 0 },
            }}
          >
            <WiredCableQualityChart data={wiredCableQualityChartSeriesData} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WiredCableQuality;
